import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, ExternalLink, Plus, X, Link2 } from "lucide-react";
import { getDigitalCard, adminUpdateDigitalCard, SHARE_IMAGE_URL } from "@/lib/api";
import { ImageUpload } from "@/admin/FormFields";

export default function DigitalCardEditor() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["digital-card"], queryFn: getDigitalCard });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data) setForm(data); }, [data, setForm]);

  const mut = useMutation({
    mutationFn: (d) => adminUpdateDigitalCard(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["digital-card"] }); toast.success("Digital card saved"); },
    onError: () => toast.error("Save failed"),
  });

  if (isLoading || !form) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, socialLinks: { ...(f.socialLinks || {}), [k]: v } }));
  const customLinks = form.customLinks || [];
  const setLink = (i, k, v) => set("customLinks", customLinks.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLink = () => set("customLinks", [...customLinks, { label: "", url: "", svg: "" }]);
  const removeLink = (i) => set("customLinks", customLinks.filter((_, idx) => idx !== i));
  const inp = "w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none text-sm";

  return (
    <div data-testid="digital-card-editor">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy">Digital Card</h1>
          <a href="/digital-card" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-civic font-medium mt-1">
            View public card <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <button onClick={() => mut.mutate(form)} disabled={mut.isPending} data-testid="save-digital-card"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-60">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </button>
      </div>

      <div className="space-y-6">
        <Card title="Identity">
          <Row label="Name"><input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="dc-name" /></Row>
          <Row label="Name (Kannada)"><input value={form.name_kn || ""} onChange={(e) => set("name_kn", e.target.value)} className={inp} data-testid="dc-name-kn" /></Row>
          <Row label="Role / Designation"><input value={form.role || ""} onChange={(e) => set("role", e.target.value)} className={inp} data-testid="dc-role" /></Row>
          <Row label="Role (Kannada)"><input value={form.role_kn || ""} onChange={(e) => set("role_kn", e.target.value)} className={inp} data-testid="dc-role-kn" /></Row>
          <Row label="Tagline"><input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className={inp} data-testid="dc-tagline" /></Row>
          <Row label="Photo"><ImageUpload value={form.photo} onChange={(v) => set("photo", v)} testid="dc-photo" /></Row>
        </Card>

        <Card title="Contact Details">
          <Row label="Phone"><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="dc-phone" /></Row>
          <Row label="WhatsApp"><input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={inp} data-testid="dc-whatsapp" /></Row>
          <Row label="Email"><input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="dc-email" /></Row>
          <Row label="Address"><textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} className={`${inp} resize-none`} data-testid="dc-address" /></Row>
          <Row label="Website URL (leave empty to use this site)"><input value={form.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://..." className={inp} data-testid="dc-website" /></Row>
        </Card>

        <Card title="Shareable Card Image">
          <p className="text-xs text-slate-500 -mt-2">
            This image is attached when citizens share the card (WhatsApp/social). It is auto-generated from the
            card details above — or upload a custom designed image to override it.
          </p>
          <Row label="Custom share image (optional override)">
            <ImageUpload value={form.shareImage} onChange={(v) => set("shareImage", v)} testid="dc-share-image" />
          </Row>
          <Row label="Current share image preview">
            <img src={`${SHARE_IMAGE_URL}?t=${form.updated_at || ""}`} alt="Share preview" className="w-64 rounded-xl border border-slate-200" data-testid="dc-share-preview" />
          </Row>
        </Card>

        <Card title="Social Links">
          {["facebook", "instagram", "twitter", "youtube", "linkedin", "threads"].map((s) => (
            <Row key={s} label={s[0].toUpperCase() + s.slice(1)}>
              <input value={form.socialLinks?.[s] || ""} onChange={(e) => setSocial(s, e.target.value)} placeholder="https://..." className={inp} data-testid={`dc-social-${s}`} />
            </Row>
          ))}
        </Card>

        <Card title="Custom Links">
          <p className="text-xs text-slate-500 -mt-2">
            Add any extra link (e.g., a new platform). Paste the platform's SVG icon code for a branded icon —
            if left empty, a generic link icon is shown.
          </p>
          {customLinks.map((l, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-4 space-y-3 relative" data-testid={`dc-custom-link-${i}`}>
              <button type="button" onClick={() => removeLink(i)} data-testid={`dc-custom-link-remove-${i}`}
                className="absolute top-3 right-3 h-7 w-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100">
                <X className="h-4 w-4" />
              </button>
              <div className="grid sm:grid-cols-2 gap-3 pr-10">
                <Row label="Label">
                  <input value={l.label || ""} onChange={(e) => setLink(i, "label", e.target.value)} placeholder="e.g. Telegram" className={inp} data-testid={`dc-custom-link-label-${i}`} />
                </Row>
                <Row label="URL">
                  <input value={l.url || ""} onChange={(e) => setLink(i, "url", e.target.value)} placeholder="https://..." className={inp} data-testid={`dc-custom-link-url-${i}`} />
                </Row>
              </div>
              <Row label="SVG icon code (optional)">
                <textarea rows={3} value={l.svg || ""} onChange={(e) => setLink(i, "svg", e.target.value)}
                  placeholder='<svg viewBox="0 0 24 24" fill="currentColor">...</svg>'
                  className={`${inp} resize-none font-mono text-xs`} data-testid={`dc-custom-link-svg-${i}`} />
              </Row>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                Icon preview:
                <span className="h-9 w-9 rounded-lg border border-slate-200 bg-navy text-slate-200 flex items-center justify-center">
                  {l.svg ? (
                    <span className="h-4 w-4 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:fill-current" dangerouslySetInnerHTML={{ __html: l.svg }} />
                  ) : (
                    <Link2 className="h-4 w-4" />
                  )}
                </span>
              </div>
            </div>
          ))}
          <button type="button" onClick={addLink} data-testid="dc-custom-link-add"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-dashed border-slate-300 text-sm text-slate-600 hover:border-gold hover:text-gold transition-colors">
            <Plus className="h-4 w-4" /> Add new link
          </button>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h2 className="font-display text-lg font-semibold text-navy mb-5">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-navy mb-1.5">{label}</label>
      {children}
    </div>
  );
}
